import { ServiceId } from "@mirage/discovery/id";
import * as services from "@mirage/discovery/services";
import { firstValueFrom } from "rxjs";

import type { Observable } from "rxjs";
import type { Service } from "./service";
import type {
  Conversation,
  ConversationMetadata,
  ConversationWithId,
  NewUserMessage,
  WorkflowAgent,
  WorkflowValues,
} from "./types";

const conversationService = services.get<Service>(ServiceId.CONVERSATION);

export default conversationService;

/**
 * Post a user message to the conversation.
 * @param newMessage The message to post.
 */
export function postUserMessage(newMessage: NewUserMessage): Promise<void> {
  return firstValueFrom(conversationService.postUserMessage(newMessage));
}

/**
 * Observe a conversation. This will emit the full conversation history as it changes.
 * You should replace the entire conversation state when this emits.
 * @param conversationId The ID of the conversation to observe.
 * @returns An observable that emits the full conversation history.
 */
export const observeConversation: (
  conversationId: string,
) => Observable<Conversation> = conversationService.observeConversation;

/**
 * Append manual messages to the conversation without submitting them to the API.
 * @param newMessages An array of user messages to append.
 */
export const appendManualMessages: (
  newMessages: NewUserMessage[],
) => Promise<void> = (newMessages: NewUserMessage[]) =>
  firstValueFrom(conversationService.appendManualMessages(newMessages));

/**
 * Get latest cached state of conversation. Call refreshConversation if we want to refresh from the server.
 * If it hasn't been loaded yet, this will trigger a load from the server.
 */
export const getConversation: (
  conversationId: string,
) => Promise<Conversation> = (conversationId: string) =>
  firstValueFrom(conversationService.getConversation(conversationId));

/**
 * Get a conversation by server conversation ID.
 */
export const getConversationByServerId: (
  serverConversationId: string,
) => Promise<ConversationWithId> = (serverConversationId: string) =>
  firstValueFrom(
    conversationService.getConversationByServerId(serverConversationId),
  );

/**
 * Create a new conversation for a conversation that does not yet exist on the server.
 */
export const createNewConversation: () => Promise<ConversationWithId> = () =>
  firstValueFrom(conversationService.createNewConversation());

/**
 * Refresh the conversation from the server. This will not resolve until the conversation has been
 * refreshed from the server.
 */
export const refreshConversation: (
  conversationId: string,
) => Promise<Conversation> = (conversationId: string) =>
  firstValueFrom(conversationService.refreshConversation(conversationId));

/**
 * Get a list of all recent conversations.
 */
export const getConversationList: () => Promise<ConversationMetadata[]> = () =>
  firstValueFrom(conversationService.getConversationList());

/**
 * Refresh the list of all recent conversations.
 */
export const refreshConversationList: () => Promise<
  ConversationMetadata[]
> = () => firstValueFrom(conversationService.refreshConversationList());

/**
 * Delete a list of conversations.
 */
export const deleteConversations: (
  conversationIds: string[],
) => Promise<void> = (conversationIds: string[]) =>
  firstValueFrom(conversationService.deleteConversations(conversationIds));

/**
 * Get metadata for a specific conversation. This may return metadata that doesn't exist in
 * getConversationList, e.g. in case where the conversation was recently created; or if the
 * conversation was not recent.
 */
export const getConversationMetadata: (
  serverConversationId: string,
) => Promise<ConversationMetadata | undefined> = (
  serverConversationId: string,
) =>
  firstValueFrom(
    conversationService.getConversationMetadata(serverConversationId),
  );

/**
 * Update the draft for a conversation.
 */
export const updateConversationDraft: (
  conversationId: string,
  draft: string,
) => Promise<Conversation> = (conversationId: string, draft: string) =>
  firstValueFrom(
    conversationService.updateConversationDraft(conversationId, draft),
  );

/**
 * Observe the list of all recent conversations as it changes.
 * @returns An observable that emits the list of all recent conversations.
 */
export const observeConversationList: () => Observable<ConversationMetadata[]> =
  conversationService.observeConversationList;

/**
 * Clear all messages from a conversation without deleting the conversation itself.
 * @param conversationId The ID of the conversation to clear.
 */
export const clearConversation: (conversationId: string) => Promise<void> = (
  conversationId: string,
) => firstValueFrom(conversationService.clearConversation(conversationId));

// CONVERSATION LOGGING (DEV ONLY)

/**
 * Set whether conversation logging is enabled.
 * @param enabled Whether to enable conversation logging.
 */
export const setConversationLoggingEnabled: (
  enabled: boolean,
) => Promise<void> = (enabled: boolean) =>
  firstValueFrom(conversationService.setConversationLoggingEnabled(enabled));

/**
 * Get whether conversation logging is enabled.
 * @returns Whether conversation logging is enabled.
 */
export const getConversationLoggingEnabled: () => Promise<boolean> = () =>
  firstValueFrom(conversationService.getConversationLoggingEnabled());

/**
 * Get all conversation logging rows.
 * @returns All conversation logging rows.
 */
export const getConversationLoggingRows: () => Promise<string[]> = () =>
  firstValueFrom(conversationService.getConversationLoggingRows());

/**
 * Clear all conversation logging rows.
 */
export const clearConversationLoggingRows: () => Promise<void> = () =>
  firstValueFrom(conversationService.clearConversationLoggingRows());

// WORKFLOW AGENTS:

/**
 * Get workflow values for a specific agent.
 * @param agentId The ID of the agent to retrieve workflow values for.
 * @returns The workflow values for the specified agent, or an empty object if none exist.
 */
export const getWorkflowAgent: (agentId: string) => Promise<WorkflowAgent> = (
  agentId: string,
) => firstValueFrom(conversationService.getWorkflowAgent(agentId));

/**
 * Set workflow values for a specific agent.
 * @param agentId The ID of the agent to set workflow values for.
 * @param values The workflow values to set for the agent.
 */
export const setWorkflowAgent: (
  agentId: string,
  values: WorkflowValues,
) => Promise<WorkflowAgent> = (agentId: string, values: WorkflowValues) =>
  firstValueFrom(conversationService.setWorkflowAgent(agentId, values));

/**
 * Get all workflow agents.
 * @returns An array of all workflow agents.
 */
export const getWorkflowAgents: () => Promise<WorkflowAgent[]> = () =>
  firstValueFrom(conversationService.getWorkflowAgents());

/**
 * Save a workflow agent.
 * @param agent The agent to save.
 */
export const saveWorkflowAgent: (
  agent: WorkflowAgent,
) => Promise<WorkflowAgent> = (agent: WorkflowAgent) =>
  firstValueFrom(conversationService.saveWorkflowAgent(agent));

/**
 * Delete a workflow agent.
 * @param agent The agent to delete.
 */
export const deleteWorkflowAgent: (agent: WorkflowAgent) => Promise<void> = (
  agent: WorkflowAgent,
) => firstValueFrom(conversationService.deleteWorkflowAgent(agent));

/**
 * Load all workflow agents.
 * @returns An array of all workflow agents.
 */
export const loadWorkflowAgents: () => Promise<WorkflowAgent[]> = () =>
  firstValueFrom(conversationService.loadApiWorkflowAgents());

// AVAILABLE MODELS:

/**
 * Get available models for conversation.
 * @returns An observable that emits the available models.
 */
export const availableModels = conversationService.availableModels;

export const startSyncAvailableModels = () =>
  firstValueFrom(conversationService.startSyncAvailableModels());

export const cancelSyncAvailableModels = () =>
  firstValueFrom(conversationService.cancelSyncAvailableModels());
